import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, RotateCcw, History, X, FileText, MessageCircle, Globe, Mail, ShieldCheck } from "lucide-react";
import { TeamStrip, CaseMeta, CHANNEL_LABEL, InternalBadge } from "@/components/AgentTeam";

const GREETING = {
  role: "assistant", agent: "Nadia",
  content: "Halo! Saya Nadia, CS & Intake CV GRCom. Ada yang bisa kami bantu — pembelian laptop/software/network/infrastruktur, atau keluhan & repair? Boleh sebutkan nama & perusahaan Anda.",
};
const CH_ICON = { web: Globe, website: Globe, whatsapp: MessageCircle, email: Mail, telegram: Send };

function Bubble({ m }) {
  if (m.role === "system") {
    return (
      <div className="flex justify-center">
        <div className="text-xs px-3 py-1.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> {m.agent}: {m.content}
        </div>
      </div>
    );
  }
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-sm px-4 py-2.5 text-sm whitespace-pre-wrap ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground border border-border"}`}>
        {!mine && m.agent && <div className="mb-1 overline text-accent">{m.agent}</div>}
        {m.content}
      </div>
    </div>
  );
}

export default function AgentChat() {
  const navigate = useNavigate();
  const [team, setTeam] = useState({ agents: [], director: "" });
  const [caseData, setCaseData] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { api.get("/agents/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [caseData, busy]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = useCallback((cid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/cases/${cid}`);
        setCaseData(data);
        if (!data.processing) { clearInterval(pollRef.current); setBusy(false); }
      } catch (err) { console.warn("Chat poll failed, retrying:", err?.message); }
    }, 2500);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setCaseData((c) => ({ ...(c || { messages: [] }), messages: [...(c?.messages || []), { role: "user", content: text }] }));
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/cases/chat", { case_id: caseData?.id || "", message: text });
      setTimeout(() => poll(data.case_id), 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const openHistory = async () => {
    try { const { data } = await api.get("/cases"); setHistory(data); setShowHistory(true); }
    catch { toast.error("Gagal memuat riwayat kasus"); }
  };
  const resume = async (cid) => {
    try {
      const { data } = await api.get(`/cases/${cid}`);
      setCaseData(data); setShowHistory(false);
      if (data.processing) { setBusy(true); poll(cid); }
    } catch { toast.error("Gagal membuka kasus"); }
  };
  const reset = () => { clearInterval(pollRef.current); setCaseData(null); setBusy(false); setInput(""); };

  const messages = caseData?.messages?.length ? caseData.messages : [GREETING];
  const current = team.agents.find((a) => a.key === (caseData?.current_agent || "nadia"));

  return (
    <div data-testid="agent-chat-page">
      <PageHeader overline="Tim AI Agent CV GRCom" title="Ruang Kasus">
        <Button variant="outline" className="rounded-sm" onClick={openHistory} data-testid="agent-history-btn"><History className="w-4 h-4 mr-2" /> Semua Kasus</Button>
        <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="agent-reset-btn"><RotateCcw className="w-4 h-4 mr-2" /> Kasus Baru</Button>
      </PageHeader>

      <div className="p-8 space-y-5">
        <TeamStrip agents={team.agents} director={team.director} active={caseData?.current_agent || "nadia"} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 border border-border bg-card rounded-sm flex flex-col h-[62vh]">
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="agent-messages">
              {messages.map((m, i) => <Bubble key={`${m.ts || "local"}-${i}`} m={m} />)}
              {busy && (
                <div className="flex justify-start" data-testid="agent-typing">
                  <div className="bg-secondary border border-border rounded-sm px-4 py-2.5 text-sm text-muted-foreground animate-pulse">
                    {current?.name || "Agent"} sedang memproses…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="border-t border-border p-3 flex gap-2">
              <Input data-testid="agent-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ketik pesan sebagai customer…" className="rounded-sm bg-secondary border-border" disabled={busy} />
              <Button data-testid="agent-send-btn" onClick={send} disabled={busy || !input.trim()} className="rounded-sm"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="space-y-4">
            <CaseMeta c={caseData} agents={team.agents} />
            {caseData?.quotation_id && (
              <Button variant="outline" className="w-full rounded-sm" data-testid="agent-open-quote" onClick={() => navigate(`/quotations/${caseData.quotation_id}`)}>
                <FileText className="w-4 h-4 mr-2" /> Buka Penawaran {caseData.quote_number}
              </Button>
            )}
          </div>
        </div>
      </div>

      {showHistory && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="agent-history-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowHistory(false)} />
          <div className="relative w-full max-w-md bg-card border-l border-border h-full overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold">Semua Kasus (Website · Telegram · Email)</h3>
              <button onClick={() => setShowHistory(false)} data-testid="agent-history-close"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>
            {history.length === 0 ? <div className="text-sm text-muted-foreground py-10 text-center">Belum ada kasus.</div> : (
              <div className="space-y-2">
                {history.map((c) => {
                  const Ic = CH_ICON[c.channel] || Globe;
                  return (
                    <button key={c.id} onClick={() => resume(c.id)} data-testid={`history-item-${c.id}`}
                      className="w-full text-left border border-border rounded-sm p-3 hover:bg-secondary transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-primary">{c.case_number}</span>
                        <span className="flex items-center gap-1 min-w-0">
                          <InternalBadge c={c} id={`history-internal-badge-${c.id}`} />
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-secondary flex items-center gap-1 shrink-0"><Ic className="w-3 h-3" />{CHANNEL_LABEL[c.channel]}</span>
                        </span>
                      </div>
                      <div className="font-medium text-sm truncate mt-1">{c.customer_company || c.customer_name || c.customer_phone || c.customer_email || "Tanpa nama"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{team.agents.find((a) => a.key === c.current_agent)?.name} · {c.status}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
